import { doesDateRangeOverlap } from 'common/api/frontend/inventory_new';
import { QueryBuilder } from 'common/db/api/utils';
import { api } from 'common/frontend/api';
import { AllocationOption, InventoryStatus, SingleInventoryItem } from 'common/modules/inventory';
import {
	GetDINOptions,
	ProductDinValues,
	OrderProduct,
	UserDetailNames,
	UserProperties,
	PurchaseTypes,
	PurchaseType,
} from 'common/types';
import { notFalsey } from 'common/utils/common';
import { getDIN } from 'common/utils/dinCalculation';
import { getUserDetailOptions } from 'common/utils/userDetails';
import { groupBy } from 'lodash';
import moment from 'moment-timezone';
import { TFunction } from 'react-i18next';

export interface OptionType {
	label: string;
	value: string;
	isOccupied: boolean;
	status: InventoryStatus;
	allocationTypes: AllocationOption[];
	inventoryItemId: string;
}

export const getSingleInventoryItemsQuery = (args: {
	shopId: string;
	locationId: string | null;
	skuId: string;
}): QueryBuilder<SingleInventoryItem> => {
	const { shopId, locationId, skuId } = args;
	const baseQuery = api()
		.inventoryItems.singleItems.where('shopId', '==', shopId)
		.where('skuId', '==', skuId);
	const query = !!locationId ? baseQuery.where('locationId', '==', locationId) : baseQuery;
	return query;
};

export const getRentalOrderProductsQuery = (args: {
	skuId: string;
	startDate: string;
}): QueryBuilder<OrderProduct> => {
	const { skuId, startDate } = args;
	/**
	 * TODO: In the future, we should query the DB with purchaseType == 'rental', but that field does not exist for most documents yet
	 *
	 * - Juuso, Aug 24th 2022
	 */
	return api()
		.orderProducts.get.whereArray('summary.skuIds', 'array-contains', skuId)
		.orderBy('unavailable.until')
		.startAt(startDate);
};

export const getSalesOrderProductsQuery = (args: { skuId: string }): QueryBuilder<OrderProduct> => {
	const { skuId } = args;
	return api()
		.orderProducts.get.whereArray('summary.skuIds', 'array-contains', skuId)
		.where('purchaseType', '==', PurchaseTypes.sales)
		.where('fulfillmentDate', '==', null);
};

export const isRelevantOrderProduct = (deps: {
	startDate: string;
	endDate: string;
	purchaseType: PurchaseType;
}) => (orderProduct: OrderProduct) => {
	const { startDate, endDate, purchaseType } = deps;
	if (orderProduct.purchaseType !== purchaseType) return false;
	switch (orderProduct.purchaseType) {
		case PurchaseTypes.rental: {
			const { from, until } = orderProduct.unavailable;

			if (orderProduct.startDate === null) return false;
			if (orderProduct.cancelled) return false;
			if (!from || !until) return false;

			const now = new Date();
			const _startDate = moment(startDate).isAfter(now) ? startDate : now.toISOString();
			if (!doesDateRangeOverlap(_startDate, endDate, from, until)) return false;
			return true;
		}
		case PurchaseTypes.sales: {
			if (orderProduct.cancelled) return false;
			if (orderProduct.fulfillmentDate !== null) return false;
			return true;
		}
	}
};

export const getProductCodesFromOrderProducts = (args: {
	orderProducts: OrderProduct[];
	currentOrderProduct: {
		id: string;
		setIndex: number | null;
	};
}): string[] => {
	const { orderProducts, currentOrderProduct } = args;
	return orderProducts
		.flatMap((product) => {
			if (product.id === currentOrderProduct.id) {
				return !!product.set
					? product.setProducts.flatMap((setProduct, index) =>
							index !== currentOrderProduct.setIndex && !setProduct.removedFromParent
								? [setProduct.productCode]
								: [],
					  )
					: [];
			}
			return !!product.set
				? product.setProducts.map((setProduct) => setProduct.productCode)
				: product.productCode;
		})
		.filter(notFalsey);
};

export const getCalculatedDinValue = (args: {
	soleLength: ProductDinValues['soleLength'];
	dinVariation: ProductDinValues['dinVariation'];
	userProperties: UserProperties;
	t: TFunction;
}): {
	din: number | null;
	error: string | null;
} => {
	const { soleLength, dinVariation, userProperties, t } = args;
	const weight = (userProperties.weight && Number(userProperties.weight.value)) || null;
	const height = (userProperties.height && Number(userProperties.height.value)) || null;
	const experienceLevel =
		(userProperties.experienceLevel && userProperties.experienceLevel.value) || null;
	const age = (userProperties.age && userProperties.age.value) || null;
	if (!weight || !height || !experienceLevel || !soleLength || !age) {
		return {
			din: null,
			error: `${t('din.missing')}: ${
				(!weight && t('common:form.weight')) ||
				(!height && t('common:form.height')) ||
				(!experienceLevel && t('common:form.experienceLevel')) ||
				(!soleLength && t('din.soleLength', 'Sole length')) ||
				(!age && t('common:form.age')) ||
				''
			}`,
		};
	}
	const experienceLevelValues = getUserDetailOptions(UserDetailNames.experienceLevel)?.values;
	const experienceNumber = (experienceLevelValues ?? []).indexOf(experienceLevel) + 1;
	if (experienceNumber <= 0) {
		return {
			din: null,
			error: `${t('din.missing')}: ${t('common:form.experienceLevel')}`,
		};
	}
	const [minAgeString, maxAgeString] = age.split('-');
	const minAge = Number(minAgeString);
	const maxAge = Number(maxAgeString);
	if ((!minAge && minAge !== 0) || (!maxAge && maxAge !== 0)) {
		return {
			din: null,
			error: `${t('din.missing')}: ${t('common:form.age')}`,
		};
	}
	const dinParams: GetDINOptions = {
		weight,
		height,
		age: maxAge || maxAge === 0 ? maxAge : minAge,
		bootSoleLength: soleLength,
		skierType: experienceNumber,
		dinVariation: dinVariation ?? null,
	};
	const dinValue = getDIN(dinParams);
	if (dinValue) {
		return {
			din: Number(dinValue),
			error: null,
		};
	}
	return {
		din: Number(dinValue) || null,
		error: dinValue ? null : t('din.calculationError', 'Not calculated, check measurements'),
	};
};

export const getOptionsFromInventoryItems = (args: {
	inventoryItems: SingleInventoryItem[];
	productCodesInUse: string[];
}): OptionType[] => {
	const { inventoryItems, productCodesInUse } = args;
	return inventoryItems.flatMap((item) => {
		if (item.status === 'SOLD' || item.status === 'LOST') return [];

		const identifiers = item.identifiers;
		const isItemOccupied = identifiers.some((id) => productCodesInUse.includes(id));

		const allocationTypes = Object.entries(item.allocation)
			.filter(([, v]) => notFalsey(v))
			.map(([k]) => k) as AllocationOption[];

		return identifiers.map((identifier) => {
			return {
				label: identifier,
				value: identifier,
				status: item.status,
				isOccupied: isItemOccupied,
				allocationTypes,
				inventoryItemId: item.id,
			};
		});
	});
};

export const sortOptions = (deps: {
	selectedIdentifier: string | undefined;
	purchaseType: PurchaseType;
}) => (optionsToSort: OptionType[]) => {
	const { selectedIdentifier, purchaseType } = deps;

	const sortFn = (a: OptionType, b: OptionType): number => {
		const sortBySelected =
			Number(b.value === selectedIdentifier) - Number(a.value === selectedIdentifier);
		if (sortBySelected !== 0) {
			return sortBySelected;
		}
		const inventoryStatusOrder = ['IN_USE', 'OUT_OF_USE', 'LOST', 'SOLD'];
		const sortByStatus =
			inventoryStatusOrder.indexOf(a.status) - inventoryStatusOrder.indexOf(b.status);
		if (sortByStatus !== 0) {
			return sortByStatus;
		}
		const sortByInUse = Number(a.isOccupied) - Number(b.isOccupied);
		if (sortByInUse !== 0) {
			return sortByInUse;
		}
		const sortByName = a.label.localeCompare(b.label);
		return sortByName;
	};

	const optionsByAllocationType = groupBy(optionsToSort, 'allocationTypes');
	const salesOptions = (optionsByAllocationType?.[PurchaseTypes.sales] ?? []).sort(sortFn);
	const rentalOptions = (optionsByAllocationType?.[PurchaseTypes.rental] ?? []).sort(sortFn);

	return purchaseType === PurchaseTypes.sales
		? [...salesOptions, ...rentalOptions]
		: [...rentalOptions, ...salesOptions];
};
